import { I18n } from '@affine/i18n';
import { css, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';

import { AffineIcon } from '../_common/icons';
import { AIPreloadConfig } from '../chat-panel/preload-config';
import {
  aiChatHeadingTypography,
  aiChatMetaTypography,
  aiChatStatusTypography,
} from '../styles/ai-chat-typography';

type AIPreloadItem = (typeof AIPreloadConfig)[number];

export const AIChatEmptyStateStyles = css`
  .ai-chat-empty-state-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .ai-chat-empty-state-title {
    ${aiChatHeadingTypography}
    color: var(--streamlain-text-primary-color);
  }

  .ai-chat-empty-state-title[data-loading='true'] {
    ${aiChatStatusTypography}
    color: var(--streamlain-text-secondary-color);
  }

  .ai-chat-empty-state-onboarding {
    display: flex;
    gap: 8px;
    flex-direction: column;
    margin-top: 16px;
  }

  .ai-chat-empty-state-onboarding-item {
    display: flex;
    height: 28px;
    gap: 8px;
    align-items: center;
    justify-content: start;
  }

  .ai-chat-empty-state-onboarding-item.interactive {
    cursor: pointer;
  }

  .ai-chat-empty-state-onboarding-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--streamlain-text-secondary-color);
  }

  .ai-chat-empty-state-onboarding-text {
    ${aiChatMetaTypography}
    color: var(--streamlain-text-primary-color);
    white-space: nowrap;
  }
`;

export function renderAIChatEmptyStateContent({
  isLoading = false,
  panelTestIds = false,
  showOnboarding = true,
  onSelect,
}: {
  isLoading?: boolean;
  panelTestIds?: boolean;
  showOnboarding?: boolean;
  onSelect?: (item: AIPreloadItem) => void;
} = {}) {
  return html`
    <div class="ai-chat-empty-state-content">
      ${AffineIcon(
        isLoading
          ? 'var(--streamlain-icon-secondary)'
          : 'var(--streamlain-primary-color)'
      )}
      <div class="ai-chat-empty-state-title" data-loading=${isLoading}>
        ${isLoading
          ? html`<span
              data-testid=${panelTestIds ? 'chat-panel-loading-state' : nothing}
              >${I18n.t('com.streamlain.ai.chat.loading-history')}</span
            >`
          : html`<span
              data-testid=${panelTestIds ? 'chat-panel-empty-state' : nothing}
              >${I18n.t('com.streamlain.ai.chat.empty-title')}</span
            >`}
      </div>
      ${showOnboarding && !isLoading
        ? html`<div
            class="ai-chat-empty-state-onboarding"
            data-testid=${panelTestIds ? 'ai-onboarding' : nothing}
          >
            ${repeat(
              AIPreloadConfig,
              config => config.textKey,
              config =>
                html`<div
                  data-testid=${panelTestIds ? config.testId : nothing}
                  class=${classMap({
                    'ai-chat-empty-state-onboarding-item': true,
                    interactive: !!onSelect,
                  })}
                  @click=${() => onSelect?.(config)}
                >
                  <div class="ai-chat-empty-state-onboarding-icon">
                    ${config.icon}
                  </div>
                  <div class="ai-chat-empty-state-onboarding-text">
                    ${I18n.t(config.textKey)}
                  </div>
                </div>`
            )}
          </div>`
        : null}
    </div>
  `;
}
